import { BOOKING_URL } from "../mock";

export const TELEMATICS = {
  seo: {
    title: "Fleet Telematics — Real-time GPS tracking, driver behaviour, fuel & temperature monitoring",
    description:
      "Fleetroot fleet telematics: live GPS tracking, driver safety scoring, fuel and engine diagnostics, temperature and door sensors, maintenance and utilisation analytics — on certified devices supplied and installed by our partner resellers across the GCC.",
  },
  hero: {
    eyebrow: "Platform · Fleet telematics",
    title: "See every vehicle. Know every driver. Control every cost.",
    subtitle:
      "Live GPS tracking, driver behaviour, fuel, temperature and maintenance on one map — from certified devices supplied and installed by our partner resellers, or the smartphone your drivers already carry.",
    primary: { label: "Request a demo", to: "/contact" },
    secondary: { label: "Book a 30-min call", href: BOOKING_URL, external: true },
    trust: ["Second-level GPS updates", "Cars, vans, trucks, buses & equipment", "Device-agnostic", "Live in days"],
  },
  problem: {
    eyebrow: "The problem",
    title: "Running a fleet blind costs you every single day.",
    text: "Without live vehicle data, the leaks are invisible until the fuel bill, the breakdown or the insurance claim arrives.",
    pains: [
      { icon: "Fuel", title: "Fuel waste", text: "Idling, detours and heavy right feet drain budgets with no visibility into the cause." },
      { icon: "Clock", title: "Vehicle misuse", text: "After-hours trips and use outside approved zones go completely undetected." },
      { icon: "Wrench", title: "Reactive maintenance", text: "Unplanned breakdowns take vehicles off the road at the worst possible moment." },
      { icon: "ShieldAlert", title: "Driver safety risk", text: "Speeding, harsh braking and fatigue go unmonitored — raising liability and premiums." },
      { icon: "Thermometer", title: "Cargo at risk", text: "Temperature excursions are discovered at the door, when the shipment is already lost." },
    ],
  },
  capabilities: {
    eyebrow: "Capabilities",
    title: "Everything your fleet tells you, in one place.",
    subtitle: "Pick a module to see what it does. Every one of them feeds the same live map, the same alerts and the same reports.",
    items: [
      {
        id: "tracking",
        label: "Live GPS tracking",
        icon: "MapPin",
        title: "Every vehicle on one live map",
        text: "Second-level positions for cars, vans, trucks, buses and heavy equipment — with trip history, geofences and ETAs for every stop.",
        bullets: ["Live map with status, speed & heading", "Trip replay with stops and timestamps", "Geofences with entry / exit alerts", "Live ETAs shared with customers"],
        visual: "fleet-map",
      },
      {
        id: "driver",
        label: "Driver behaviour",
        icon: "Gauge",
        title: "Safer drivers, lower premiums",
        text: "Score every driver on speeding, harsh braking, acceleration, cornering and idling. Rank, coach and reward from one dashboard.",
        bullets: ["Per-driver safety score and ranking", "Harsh event detection with location", "Speeding vs road limits", "Driver ID via app, iButton or RFID"],
        visual: "driver-score",
      },
      {
        id: "fuel",
        label: "Fuel & engine",
        icon: "Fuel",
        title: "Fuel, engine hours and fault codes",
        text: "Read fuel level, consumption, RPM, odometer and diagnostic codes directly from the vehicle via CAN-bus or fuel sensors.",
        bullets: ["Consumption per vehicle, driver and route", "Refuel and fuel-drop detection", "Engine hours and odometer sync", "Fault codes (DTC) before the breakdown"],
        visual: "fuel",
      },
      {
        id: "sensors",
        label: "Temperature & sensors",
        icon: "Thermometer",
        title: "Cold chain you can prove",
        text: "Temperature, humidity and door sensors for reefers and cargo boxes — with instant alerts and compliance logs for pharma and food.",
        bullets: ["Multi-zone temperature & humidity", "Door open / close events", "Threshold alerts in seconds", "Audit-ready trip reports"],
        visual: "cold-chain",
      },
      {
        id: "maintenance",
        label: "Maintenance & utilisation",
        icon: "Wrench",
        title: "Service on schedule, assets fully used",
        text: "Plan maintenance by kilometres, engine hours or date, and spot the vehicles that sit idle while others run flat out.",
        bullets: ["Service reminders by km, hours or date", "Utilisation and idle-time reports", "Cost per km per vehicle", "Documents, insurance & registration expiry"],
        visual: "maintenance",
      },
    ],
  },
  platform: {
    eyebrow: "How the platform works",
    title: "Device-agnostic by design. One platform, whatever is in the vehicle.",
    subtitle:
      "Fleetroot is the layer above the hardware. Plug in certified trackers, the vehicle's own CAN-bus, sensors, a smartphone — or the devices you already own — and every signal lands in one data model, one rule engine and one login.",
    steps: [
      { n: "01", title: "Connect any device", text: "GPS trackers, OBD-II and CAN-bus readers, temperature and door sensors, driver smartphones — and most existing trackers via their native protocols. No rip and replace." },
      { n: "02", title: "Normalise & enrich", text: "Every signal is translated into one data model, matched to the vehicle, driver and trip, and enriched with maps, speed limits and geofences." },
      { n: "03", title: "Decide & act", text: "The rule engine and AI models turn raw data into alerts, safety scores, service reminders, ETAs and dispatch decisions — in seconds, not reports." },
      { n: "04", title: "Share everywhere", text: "Live map and driver app, scheduled reports, WhatsApp / email alerts, and a REST API with webhooks that feed your ERP, TMS or BI tools." },
    ],
    note: "Need hardware? Our partner resellers supply and install certified devices and sensors across the GCC.",
  },
  alerts: {
    eyebrow: "Alerts & automation",
    title: "Know the moment something goes wrong.",
    subtitle: "Set the rules once. Fleetroot watches every vehicle around the clock and notifies the right person on the right channel.",
    list: ["Geofence entry / exit", "Speeding", "Harsh braking & acceleration", "Excessive idling", "After-hours ignition", "Route deviation", "Temperature threshold", "Door open", "Fuel drop", "Service due", "Device offline / power cut", "SOS button"],
    channels: ["Dashboard", "Email", "WhatsApp & SMS", "Webhooks & API"],
  },
  reports: {
    eyebrow: "Reports & KPIs",
    title: "Turn vehicle data into decisions.",
    subtitle: "Scheduled reports and live dashboards for operations, finance and HSE — with the KPIs each of them actually asks for.",
    kpis: [
      { value: "Cost / km", label: "fuel, maintenance and driver time per vehicle" },
      { value: "Utilisation", label: "hours driven vs available, by vehicle and depot" },
      { value: "Idle time", label: "minutes and fuel burned standing still" },
      { value: "Safety score", label: "per driver, per team, per month" },
      { value: "On-time rate", label: "arrivals vs promised windows" },
      { value: "Fuel / 100 km", label: "benchmarked across vehicles and routes" },
    ],
  },
  industries: {
    eyebrow: "Built for every fleet",
    title: "Whatever you run, Fleetroot keeps it visible.",
    items: [
      { icon: "Truck", title: "Logistics & distribution", text: "Trucks and vans across depots with ETAs, utilisation and cost per drop." },
      { icon: "Bus", title: "Passenger & school transport", text: "Buses on schedule, drivers scored, parents and operators informed." },
      { icon: "HardHat", title: "Construction & equipment rental", text: "Engine hours, location and utilisation for assets across job sites." },
      { icon: "Pill", title: "Pharma & food cold chain", text: "Temperature-proven deliveries with audit-ready records." },
      { icon: "Wrench", title: "Field service", text: "Technician vehicles tracked, jobs proven, routes optimised." },
      { icon: "KeyRound", title: "Car rental & leasing", text: "Protect assets, detect misuse and automate maintenance per contract." },
    ],
  },
  results: {
    eyebrow: "Typical outcomes",
    title: "The KPIs fleet telematics moves.",
    subtitle: "Typical improvements fleets see once vehicles, drivers and sensors report into one platform. Your baseline decides the exact numbers — we'll measure them with you.",
    items: [
      { value: "10–20%", label: "lower fuel cost", who: "less idling, fewer detours, smoother driving" },
      { value: "-30%", label: "unplanned downtime", who: "service by km and engine hours, fault codes early" },
      { value: "+25%", label: "driver safety score", who: "harsh-event coaching and speeding alerts" },
      { value: "+15%", label: "vehicle utilisation", who: "idle assets found and redeployed" },
    ],
  },
  faq: [
    { q: "Do I need new hardware?", a: "Not necessarily. The driver app turns any smartphone into a tracker. For vehicle-level data — ignition, fuel, engine hours, temperature — our partner resellers supply and install certified GPS devices and sensors." },
    { q: "Can Fleetroot use the GPS trackers I already have?", a: "In most cases, yes. Fleetroot is device-agnostic and connects to most major tracker protocols, so you can keep existing hardware and migrate gradually." },
    { q: "How often does the location update?", a: "Certified trackers report at second-level intervals while moving, with smart reporting when parked to save data and battery. The driver app reports every few seconds while a trip is active." },
    { q: "Does it work across GCC countries?", a: "Yes. Devices ship with regional SIMs and the platform is used by fleets operating across the UAE, Saudi Arabia, Qatar and Oman." },
    { q: "Can telematics and delivery management work together?", a: "They are the same platform. Vehicle data from telematics feeds routing, dispatch and ETAs, so you manage fleet and deliveries from one login." },
    { q: "How long does installation take?", a: "Light fleets are live the same day with the driver app. Hardware installation is scheduled through our partner resellers and typically takes under an hour per vehicle." },
  ],
  cta: {
    title: "See your fleet live, this week.",
    text: "Tell us what you run and how many vehicles. We'll set up a live demo on your routes and quote installation within 24 hours.",
    cta: { label: "Request a demo", to: "/contact" },
    secondary: { label: "Book a 30-min call", href: BOOKING_URL, external: true },
  },
};
